import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api-client";
import { MessageSquare, Mail, Send, CheckCircle2, ShieldCheck, Users, Radio } from "lucide-react";
import { toast } from "sonner";

export default function CommunicationsPage() {
  const queryClient = useQueryClient();
  const [channel, setChannel] = useState("EMAIL");
  const [audience, setAudience] = useState("all_volunteers@miracle.org");
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");

  const { data: logs = [], isLoading } = useQuery({
    queryKey: ["comm-logs"],
    queryFn: async () => {
      const res = await apiClient.get("/communications/logs");
      return res.data;
    },
  });

  const sendBroadcastMutation = useMutation({
    mutationFn: async (data) => {
      const res = await apiClient.post("/communications/broadcast", data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["comm-logs"]);
      toast.success(`${channel} broadcast sent successfully!`);
      setTitle("");
      setContent("");
    },
    onError: (err) => {
      toast.error(err?.response?.data?.reason || "Failed to dispatch broadcast");
    },
  });

  const handleSend = (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    sendBroadcastMutation.mutate({ channel, audience, title, content });
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6 font-sans">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-200 pb-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Radio className="w-7 h-7 text-indigo-600" />
            Multichannel Communications Hub
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Targeted SMS (Twilio) & Email (SendGrid) campaign dispatch with consent opt-out checking.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Broadcast Composer */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-5 shadow-sm">
          <h2 className="text-sm font-bold text-slate-900 flex items-center gap-2">
            <Send className="w-4 h-4 text-indigo-600" />
            Compose Multichannel Broadcast
          </h2>

          <form onSubmit={handleSend} className="space-y-4">
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">Select Channel</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setChannel("EMAIL")}
                  className={`py-2 px-3 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 border transition-all ${
                    channel === "EMAIL"
                      ? "bg-indigo-600 text-white border-indigo-600"
                      : "bg-slate-50 text-slate-700 border-slate-200"
                  }`}
                >
                  <Mail className="w-3.5 h-3.5" /> Email (SendGrid)
                </button>
                <button
                  type="button"
                  onClick={() => setChannel("SMS")}
                  className={`py-2 px-3 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 border transition-all ${
                    channel === "SMS"
                      ? "bg-indigo-600 text-white border-indigo-600"
                      : "bg-slate-50 text-slate-700 border-slate-200"
                  }`}
                >
                  <MessageSquare className="w-3.5 h-3.5" /> SMS (Twilio)
                </button>
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-slate-700">Target Audience Group / Recipient</label>
              <input
                type="text"
                value={audience}
                onChange={(e) => setAudience(e.target.value)}
                className="w-full mt-1 px-3.5 py-2 border border-slate-200 rounded-lg text-xs"
              />
            </div>

            {channel === "EMAIL" && (
              <div>
                <label className="text-xs font-medium text-slate-700">Email Subject</label>
                <input
                  required
                  type="text"
                  placeholder="e.g. Urgent Update: Weekend Food Distribution Shift"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full mt-1 px-3.5 py-2 border border-slate-200 rounded-lg text-xs"
                />
              </div>
            )}

            <div>
              <label className="text-xs font-medium text-slate-700">Message Body / Content</label>
              <textarea
                required
                rows={4}
                placeholder="Type your message content here..."
                value={content}
                onChange={(e) => setContent(e.target.value)}
                className="w-full mt-1 px-3.5 py-2 border border-slate-200 rounded-lg text-xs"
              />
            </div>

            <button
              type="submit"
              disabled={sendBroadcastMutation.isPending}
              className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-semibold text-xs rounded-xl shadow-sm flex items-center justify-center gap-1.5"
            >
              <Send className="w-4 h-4" />
              {sendBroadcastMutation.isPending ? "Dispatching..." : `Send ${channel} Broadcast`}
            </button>
          </form>
        </div>

        {/* Message Delivery Logs */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-6 space-y-4 shadow-sm">
          <h2 className="text-sm font-bold text-slate-900">Multichannel Delivery Logs</h2>

          {logs.length === 0 ? (
            <div className="p-12 text-center text-xs text-slate-400">
              No messages dispatched yet. Send your first broadcast above!
            </div>
          ) : (
            <div className="space-y-3">
              {logs.map((log) => (
                <div key={log.id} className="p-3.5 rounded-xl border border-slate-200 bg-slate-50/50 space-y-1">
                  <div className="flex justify-between items-center text-xs">
                    <span className="font-bold text-slate-900 flex items-center gap-1.5">
                      {log.channel === "EMAIL" ? <Mail className="w-3.5 h-3.5 text-indigo-600" /> : <MessageSquare className="w-3.5 h-3.5 text-emerald-600" />}
                      To: {log.audience}
                    </span>
                    <span className="text-[10px] font-mono text-slate-400">
                      {new Date(log.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                  {log.title && <p className="text-xs font-semibold text-slate-700">{log.title}</p>}
                  <p className="text-xs text-slate-600 italic leading-relaxed">{log.content}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
